import React, { useEffect, useMemo, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import moment from "moment"
import eye from "../../../assets/images/icons/eye.svg"
import { Link } from "react-router-dom"
import AddInvoiceModal from "modals/addInvoiceModal"
import {
  formatAmount,
  getInvoiceStatus,
  getTableSortingHeaderIcon,
} from "helpers/common"
import { useLazyGetInvoicesWpQuery } from "store/api/wealthpartnerapi/invoices/invoice.api"
import OutsideClickPopover from "modals/OutsideClickPopover"

const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
}

const InvoiceManagementTable = () => {
  const [getInvoices, { isFetching }] = useLazyGetInvoicesWpQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [openInvoice, setOpenInvoice] = useState(false)

  //meta title
  document.title = "Invoice Management"

  useEffect(() => {
    fetchInvoices()
  }, [filters])

  const fetchInvoices = async () => {
    try {
      const payload = {
        ...filters,
        start_date: filters.created_at?.[0]
          ? moment(filters.created_at[0]).format("YYYY-MM-DD")
          : undefined,
        end_date: filters.created_at?.[1]
          ? moment(filters.created_at[1]).format("YYYY-MM-DD")
          : undefined,
      }
      delete payload.created_at
      if (!payload.status) delete payload.status

      const { data } = await getInvoices(payload).unwrap()
      setData(data?.rows || data || [])
      setCount(data?.count || (data?.rows?.length ?? 0))
    } catch (err) {
      console.error("Failed to fetch invoices:", err)
    }
  }

  const onRefresh = () => {
    setOpenInvoice(false)
    setFilters({ ...defaultFilters, timestamp: moment().unix() })
  }

  const columns = useMemo(
    () => [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        id: "created_at",
        accessorFn: (row) =>
          row?.created_at ? new Date(row.created_at).getTime() : null,
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) =>
          moment(row.original.created_at).format("D MMM YYYY, h:mm A"),
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Title"),
        accessorKey: "title",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row?.original?.title,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Code"),
        accessorKey: "unique_code",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>{row?.original?.unique_code}</>,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
        accessorKey: "amount",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => <>₹ {formatAmount(row?.original?.amount)}</>,
      },
      {
        header: "File",
        accessorKey: "file",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <a
            href={row.original.file_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={eye} alt="View File" />
          </a>
        ),
      },
      {
        header: () => <div className="flex items-center gap-2">Status</div>,
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => {
          const { status, reason, id } = row.original

          let rejectInfo = ""
          if (status === "REJECTED") {
            const tooltipId = `rejectInfoTooltip-${id}`

            rejectInfo = (
              <Link to="#" id={tooltipId} className="action-view ms-2">
                <i
                  className="mdi mdi-information-outline"
                  style={{ color: "#8F8C8C" }}
                />
                <OutsideClickPopover
                  target={tooltipId}
                  title="Reason for Rejection"
                  body={reason}
                />
              </Link>
            )
          }

          return (
            <div className="code-status">
              {getInvoiceStatus(row?.original?.status)}
              {rejectInfo}
            </div>
          )
        },
      },
    ],
    []
  )

  const FilterComponents = () => (
    <>
      <Link
        className="btn btn-secondary"
        to="#"
        onClick={() => setOpenInvoice(true)}
      >
        <i className="mdi mdi-plus me-1"></i> Create Invoice
      </Link>
    </>
  )

  return (
    <>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        FilterComponents={FilterComponents}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />
      {openInvoice && (
        <AddInvoiceModal
          open={openInvoice}
          setOpen={setOpenInvoice}
          refresh={onRefresh}
        />
      )}
    </>
  )
}

export default InvoiceManagementTable
